/**
 * Latest Posts Component
 *
 * Light DOM Web Component that handles AJAX loading of additional posts.
 * Uses Light DOM (no Shadow DOM) since it operates on existing server-rendered markup
 * and doesn't need style encapsulation.
 *
 * Usage: <latest-posts class="...">...existing markup...</latest-posts>
 *
 * @package Felix
 * @since 1.0.0
 */

class LatestPosts extends HTMLElement {
	connectedCallback() {
		this.postsContainer = this.querySelector('[data-latest-posts-container]')
		this.loadMoreBtn = this.querySelector('.latest-posts__load-more')

		if (!this.postsContainer || !this.loadMoreBtn) {
			return
		}

		this.perPage = parseInt(this.postsContainer.dataset.loadMoreCount, 10) || 4
		this.categoryId = parseInt(this.postsContainer.dataset.categoryId, 10) || 0
		this.authorId = parseInt(this.postsContainer.dataset.authorId, 10) || 0
		this.medicalExpertId = parseInt(this.postsContainer.dataset.medicalExpertId, 10) || 0
		this.excludedCategories = (this.postsContainer.dataset.excludedCategories || '')
			.split(',')
			.map((id) => parseInt(id, 10))
			.filter((id) => id > 0)
		this.offset = this.postsContainer.querySelectorAll('.post-card').length
		this.isLoading = false

		this.init()
	}

	init() {
		this.loadMoreBtn.addEventListener('click', (e) => {
			e.preventDefault()
			this.loadMore()
		})
	}

	async loadMore() {
		if (this.isLoading) {
			return
		}

		this.isLoading = true
		this.loadMoreBtn.classList.add('is-loading')
		this.loadMoreBtn.disabled = true

		try {
			let url = `/wp-json/felix/v1/posts?offset=${this.offset}&per_page=${this.perPage}`
			if (this.categoryId) {
				url += `&category=${this.categoryId}`
			} else if (this.authorId) {
				url += `&author=${this.authorId}`
			} else if (this.medicalExpertId) {
				url += `&medical_expert=${this.medicalExpertId}`
			}
			if (this.excludedCategories.length) {
				url += `&excluded_categories=${this.excludedCategories.join(',')}`
			}

			const response = await fetch(url, {
				headers: {
					'Content-Type': 'application/json',
				},
			})

			if (!response.ok) {
				throw new Error('Network response was not ok')
			}

			const data = await response.json()

			if (data.html) {
				// Insert new posts
				this.postsContainer.insertAdjacentHTML('beforeend', data.html)
				this.offset = data.offset
			}

			// Hide button if no more posts
			if (!data.hasMore) {
				this.loadMoreBtn.style.display = 'none'
			}
		} catch (error) {
			console.error('Error loading more posts:', error)
		} finally {
			this.isLoading = false
			this.loadMoreBtn.classList.remove('is-loading')
			this.loadMoreBtn.disabled = false
		}
	}
}

customElements.define('latest-posts', LatestPosts)
